import { useCallback, useEffect, useMemo, useState } from "react";
import { DockerContainerLogsResponse, DockerContainerStatusResponse, getDockerContainerLogs, getDockerContainerStatus, getImportReport, getLiveTrades, getReport, saveTradeAccountCredentials, TradeAccountCredentials } from "../function/api";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import Layout from "Layout";
import { Button, DatePicker, Modal, Tooltip } from "antd";
import { Download } from "lucide-react";

// Blocks / Components
import UtilitiesButtons from "../component/utilitiesButtons";
import TradeAccountInfo from "../component/TradeAccountInfo";
import TradingStatistics from "../component/TradingStatistics";
//import ConsistencyScore from "../component/ConsistencyScore";
import EquitySimulation from "../component/EquitySimulation";
import TradeHistoryList from "../component/TradeHistory";
import DailySummary from "../component/DailySummary";
import BehavioralAnalysis from "../component/BehavioralAnalysis";
import Footer from "../component/Footer";
import CredentialsModal, { ContainerStatusModal, CredentialsConfirmationModal } from "../component/CredentialsModal";

// Charts
import HightChart from "../component/HightChart";
import DashboardCharts from "../component/ChartBar";
import PerformanceChart from "../component/PerformanceChart";
import DashboardCharts2 from "../component/ChartBar2";
import DashboardCharts3 from "../component/ChartBar3";

// Styles
import "../AccountLiveMetriX.scss";
import "../styles.scss";
import { CaretLeftIcon, EyeIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { convertToLocalTime } from "pages/Dashboard/Metrix_v2/function/helper";
import MainAccountBalanceChart from "pages/Dashboard/Metrix_v2/component/main_account_balance_chart";
import ConsistencyScore from "pages/Dashboard/Metrix_v2/component/ConsistencyScore";
import AccountGoals from "pages/Dashboard/Metrix_v2/component/AccountGoals";
import { API_URL } from "env";

const DashBoardMetrix = () => {

    const createCredentialState = (account = '', platform = ''): TradeAccountCredentials => ({
        account,
        password: '',
        server: '',
        platform,
        ctraderId: ''
    });

    const { tradePlatform, id } = useParams()
    const { t, i18n } = useTranslation();
    const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState<boolean>(false);
    const [accountCredentials, setAccountCredentials] = useState<TradeAccountCredentials>(createCredentialState(id || '', tradePlatform || ''));
    const [isCredentialsSaving, setIsCredentialsSaving] = useState<boolean>(false);
    const [credentialsError, setCredentialsError] = useState<string>('');
    const [isCredentialsConfirmationOpen, setIsCredentialsConfirmationOpen] = useState<boolean>(false);
    const [isBotRunning, setIsBotRunning] = useState<boolean>(false);
    const [hasContainerStatus, setHasContainerStatus] = useState<boolean>(false);
    const [isContainerStatusModalOpen, setIsContainerStatusModalOpen] = useState<boolean>(false);
    const [containerStatus, setContainerStatus] = useState<DockerContainerStatusResponse | null>(null);
    const [containerLogs, setContainerLogs] = useState<DockerContainerLogsResponse | null>(null);
    const [isContainerStatusLoading, setIsContainerStatusLoading] = useState<boolean>(false);
    const [containerStatusError, setContainerStatusError] = useState<string>('');

    // State for personal numbers
    const [showPersonalInfo, setShowPersonalInfo] = useState(false);
    const [personalNumbers, setPersonalNumbers] = useState({
        personalYearNumber: null,
        personalMonthNumber: null,
        personalDayNumber: null
    });

    const [personalNumberMessage, setPersonalNumberMessage] = useState({
        year: '',
        month: '',
        day: ''
    });

    // State for date filters
    const [closeTimeFrom, setCloseTimeFrom] = useState<string>('');
    const [closeTimeTo, setCloseTimeTo] = useState<string>('');
    // Memoize dayjs values to prevent recreation on every render
    const dayjsFrom = useMemo(() => {
        return (closeTimeFrom && closeTimeFrom !== '*' && closeTimeFrom !== '') ? dayjs(closeTimeFrom) : null
    }, [closeTimeFrom])

    const dayjsTo = useMemo(() => {
        return (closeTimeTo && closeTimeTo !== '*' && closeTimeTo !== '') ? dayjs(closeTimeTo) : null
    }, [closeTimeTo])

    // data states 
    const [report, setReport] = useState<any>(null);
    const [liveTrade, setLiveTrade] = useState<any>(null);
    const [sectionChartData, setSectionChartData] = useState<{
        currentBalance: number;
        equity: number;
        profitLoss: number;
        chartData: {
            labels: string[];
            datasets: number[];
        };
        chartPoints: {
            balance: number;
            targetBalance: number;
            maxloss: number;
            balanceSize: number;
        };
    }>(
        {
            currentBalance: 0,
            equity: 0,
            profitLoss: 0,
            chartData: {
                labels: [],
                datasets: [],
            },
            chartPoints: {
                balance: 0,
                targetBalance: 0,
                maxloss: 0,
                balanceSize: 0,
            }
        }
    );


    const fetchPersonalNumbers = useCallback(async () => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return;

            const userObject = JSON.parse(userData);
            const dob = userObject.birthday;
            if (!dob) return;

            let formattedDob: string;
            const date = new Date(dob);
            if (isNaN(date.getTime())) {
                formattedDob = dob;
            } else {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                formattedDob = `${day}/${month}/${year}`;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No token found in localStorage');
                return;
            }
            const language = localStorage.getItem('i18nextLng') || 'en';
            const response = await fetch(`${API_URL}/api/v1/be/coaching-daily-personal?birthday=${formattedDob}&locale=${language}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            setPersonalNumbers({
                personalYearNumber: data.data.personal_year.number,
                personalMonthNumber: data.data.personal_month.number,
                personalDayNumber: data.data.personal_day.number
            });
            setPersonalNumberMessage({
                year: data.data.personal_year.items[0].description,
                month: data.data.personal_month.items[0].description,
                day: data.data.personal_day.items[0].description
            });
        } catch (error) {
            console.error('Error fetching personal numbers:', error);
        }
    }, []);


    useEffect(() => {
        if (report) {

            const equityCurve = report.equity_analysis?.equity_curve || [];

            const chartData = {
                labels: equityCurve.map((item: { date?: string | Date }) =>
                    item.date ? new Date(item.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : ""
                ),
                datasets: equityCurve.map((item: { balance?: number | string }) => Number(item.balance || 0)),
            };

            const currentBalance = Number(report.performance_summary?.current_balance || 0);
            const equity = Number(report.performance_summary?.current_equity || 0);

            // const profitLoss = liveTrade.reduce((acc: number, trade: { profit?: number; commission?: number; swap?: number }) => {
            //     const net = Number(trade.profit || 0);
            //     const comm = Number(trade.commission || 0);
            //     const swap = Number(trade.swap || 0);
            //     // Loại bỏ hoa hồng và phí swap để ra lợi nhuận chưa tính commission
            //     return acc + (net - comm - swap);
            // }, 0);

            const baseBalance = Number(report.account_info?.initial_balance || (equityCurve.length > 0 ? equityCurve[0].balance : 0));

            const chartPoints = {
                balance: currentBalance,
                targetBalance: baseBalance * 1.05,
                maxloss: baseBalance * 0.9,
                balanceSize: baseBalance,
            };

            setSectionChartData({
                chartData,
                currentBalance,
                equity,
                profitLoss: 0,
                chartPoints,
            });
        }
    }, [report]); // Note: Added liveTrade to dependency array if it's used inside

    useEffect(() => {
        setAccountCredentials((prev) => ({
            ...prev,
            account: id || prev.account,
            platform: tradePlatform || prev.platform
        }));
    }, [id, tradePlatform]);

    useEffect(() => {
        if (!id || !tradePlatform) {
            setIsBotRunning(false);
            setHasContainerStatus(false);
            return;
        }

        let isMounted = true;

        const syncDockerStatus = async () => {
            try {
                const response = await getDockerContainerStatus(tradePlatform, id);
                const state = response?.status?.State?.toLowerCase();
                if (isMounted) {
                    setHasContainerStatus(Boolean(response));
                    setIsBotRunning(state === 'running');
                }
            }
            catch (error) {
                if (isMounted) {
                    setHasContainerStatus(false);
                    setIsBotRunning(false);
                }
            }
        };

        syncDockerStatus();

        return () => {
            isMounted = false;
        };
    }, [id, tradePlatform]);

    // Flag 
    const isNoData = useMemo(() => {
        return (
            (!liveTrade || liveTrade.data.length === 0) && (report?.today_balance == null)
        );
    }, [liveTrade, report]);

    const onInit = async () => {
        try {
            const reportData = await getReport(id || '', closeTimeFrom, closeTimeTo);
            setReport(reportData.data || {});
            const liveTradesData = await getLiveTrades(id || '', 100, 1);
            setLiveTrade(liveTradesData || {});
        }
        catch (error) {
            console.error('Error during initialization:', error);
        }
    };
    // Executed on init
    useEffect(() => {
        onInit();
        fetchPersonalNumbers();
        const timer = setTimeout(() => {
            onInit();
        }, 10 * 60 * 1000); // 10 minutes
        return () => clearTimeout(timer);
    }, [id, closeTimeFrom, closeTimeTo]);

    useEffect(() => {
        if (!isCredentialsModalOpen) {
            return;
        }

        setCredentialsError('');
        setAccountCredentials((prev) => ({
            ...prev,
            account: id || '',
            platform: tradePlatform || '',
            password: '',
            server: '',
            ctraderId: ''
        }));
    }, [id, isCredentialsModalOpen, tradePlatform]);

    const handleCredentialFieldChange = (field: keyof TradeAccountCredentials, value: string) => {
        setAccountCredentials((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const openCredentialsModal = () => {
        setContainerStatusError('');
        setIsContainerStatusModalOpen(false);
        setIsCredentialsModalOpen(true);
    };

    const handleAddCredentialsClick = async () => {
        if (!id || !tradePlatform) {
            openCredentialsModal();
            return;
        }

        setIsContainerStatusLoading(true);
        setContainerStatusError('');

        try {
            const statusResponse = await getDockerContainerStatus(tradePlatform, id);
            if (!statusResponse) {
                setHasContainerStatus(false);
                setContainerStatus(null);
                setContainerLogs(null);
                openCredentialsModal();
                return;
            }

            setHasContainerStatus(true);
            setContainerStatus(statusResponse);

            try {
                const logsResponse = await getDockerContainerLogs(tradePlatform, id, 10);
                setContainerLogs(logsResponse);
                setContainerStatusError('');
            }
            catch (error) {
                setContainerLogs(null);
                setContainerStatusError(t('Unable to load container logs.'));
            }

            setIsCredentialsModalOpen(false);
            setIsContainerStatusModalOpen(true);
        }
        catch (error) {
            setHasContainerStatus(false);
            setContainerStatus(null);
            setContainerLogs(null);
            openCredentialsModal();
        }
        finally {
            setIsContainerStatusLoading(false);
        }
    };

    const handleRestartCredentialsFlow = () => {
        setIsContainerStatusModalOpen(false);
        setContainerStatusError('');
        setIsCredentialsModalOpen(true);
    };

    const handleCredentialsSave = async () => {
        if (!id) {
            setCredentialsError(t('Account not found.'));
            return;
        }

        setIsCredentialsSaving(true);
        setCredentialsError('');

        try {
            await saveTradeAccountCredentials(id, accountCredentials);
            const platform = accountCredentials.platform || tradePlatform;
            if (platform) {
                const statusResponse = await getDockerContainerStatus(platform, id);
                setHasContainerStatus(Boolean(statusResponse));
                setIsBotRunning(statusResponse?.status?.State?.toLowerCase() === 'running');
            }
            setIsCredentialsModalOpen(false);
            setIsCredentialsConfirmationOpen(true);
        }
        catch (error) {
            setCredentialsError(t('Lumir_bot.message.error'));
        }
        finally {
            setIsCredentialsSaving(false);
        }
    };

    return (
        <>
            <Helmet>
                {/* HTML Meta Tags */}
                <title>
                    Account Live MetriX {id ? id : ''} - Dashboard
                </title>
                <meta name="description" content={`Account Live MetriX for ${id} on ${tradePlatform}`} />

                {/* Facebook Meta Tags */}
                <meta property="og:url" content={``} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={`Account Live MetriX ${id ? id : ''} - Dashboard`} />
                <meta property="og:description" content={`Account Live MetriX for ${id} on ${tradePlatform}`} />

                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:domain" content="" />
                <meta property="twitter:url" content="" />
                <meta name="twitter:title" content={`Account Live MetriX ${id ? id : ''} - Dashboard`} />
                <meta name="twitter:description" content={`Account Live MetriX for ${id} on ${tradePlatform}`} />
            </Helmet>
            <>
                {/* Nhúng CSS trực tiếp */}
                <style>{`
                    .metrix-v2-container {
                    font-family: 'Inter', sans-serif;
                    background-color: #F4F4F4; /* bg-slate-50 */
                    color: #1e293b; /* text-slate-800 */
                    }

                    .font-mono-num {
                    font-family: 'JetBrains Mono', 'Inter', monospace;
                    font-variant-numeric: tabular-nums;
                    }

                    /* Scrollbar */
                    ::-webkit-scrollbar {
                    width: 5px;
                    height: 5px;
                    }
                    ::-webkit-scrollbar-track {
                    background: transparent;
                    }
                    ::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                    }

                    .shadow-premium {
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.01);
                    }

                    .tab-active {
                    color: #0f172a;
                    font-weight: 600;
                    border-bottom: 2px solid #2563eb;
                    }

                    .tab-inactive {
                    color: #64748b;
                    border-bottom: 2px solid transparent;
                    transition: color 0.2s, border-bottom 0.2s;
                    }
                    .tab-inactive:hover {
                    color: #334155;
                    border-bottom: 2px solid #cbd5e1;
                    }

                    input[type="number"]::-webkit-inner-spin-button,
                    input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                    }

                    @keyframes pulse-dot {
                    0% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
                    }
                    70% {
                        transform: scale(1.1);
                        box-shadow: 0 0 0 4px rgba(16, 185, 129, 0);
                    }
                    100% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
                    }
                    }

                    .live-dot {
                    animation: pulse-dot 2s infinite;
                    }

                    /* Tailwind colors equivalent */
                    .bg-brand-50 { background-color: #eff6ff; }
                    .text-brand-600 { color: #2563eb; }
                    .border-brand-100 { border-color: #dbeafe; }
                `}</style>

                {/* Sử dụng class như bình thường */}
                <div className="metrix-v2-container">
                    <span className="live-dot"></span>
                </div>
            </>
            <Layout>
                <div className={`_tw account-metrix`}>
                    {/* Breadcrumb Navigation */}
                    <div className="_breadcrumbContainer_x7nfb_3 mb-6 mr-0 sm:mx-0 flex justify-between items-center pdf-section hide-mod">
                        <button
                            onClick={() => {
                                window.history.back()
                            }}
                            type="button" className="border border-gray-200 !px-3 sm:px-0 inline-flex items-center gap-2 rounded-lg bg-white py-[6px] transition hover:!border-gray-500 w-[120px] !text-[14px]">
                            <CaretLeftIcon size={18} />
                            {t('Back')}
                        </button>
                        {/* <nav className="_breadcrumbWrapper_x7nfb_38" style={{ margin: 0, alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="_homeIcon_x7nfb_95">
                                    <g clipPath="url(#clip0_17_303)">
                                        <path
                                            d="M23.4923 8.29219L12.4816 2.12579C12.1856 1.95807 11.8206 1.95807 11.5147 2.12579L0.503991 8.29219C0.109342 8.50924 -0.0781117 8.96309 0.0304168 9.40707C0.138945 9.84118 0.53358 10.147 0.987427 10.147H2.20098V20.3191C2.20098 20.8618 2.64496 21.3057 3.18761 21.3057C3.73025 21.3057 4.17423 20.8618 4.17423 20.3191V9.16041C4.17423 8.95322 4.11502 8.76576 3.99663 8.59804L11.9981 4.11877L19.9996 8.59804C19.8911 8.7559 19.8221 8.95322 19.8221 9.16041V20.3191C19.8221 20.8618 20.266 21.3057 20.8087 21.3057C21.3513 21.3057 21.7953 20.8618 21.7953 20.3191V10.147H23.0089C23.4627 10.147 23.8475 9.84118 23.9659 9.40707C24.0843 8.97295 23.8869 8.50924 23.4923 8.29219Z"
                                            fill="currentColor"
                                        ></path>
                                        <path
                                            d="M11.9981 11.2126C10.2025 11.2126 8.74225 12.6728 8.74225 14.4684V20.3191C8.74225 20.7236 9.07771 21.0591 9.48222 21.0591H14.514C14.9185 21.0591 15.254 20.7236 15.254 20.3191V14.4684C15.254 12.6728 13.7938 11.2126 11.9981 11.2126ZM13.774 19.5791H10.2222V14.4684C10.2222 13.4917 11.0214 12.6925 11.9981 12.6925C12.9749 12.6925 13.774 13.4917 13.774 14.4684V19.5791Z"
                                            fill="currentColor"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_17_303"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                                    </defs>
                                </svg>
                                <a href="/accounts-overview" className="text-labels-medium _link_x7nfb_12">{t('Trader')}</a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg width="7" height="17" viewBox="0 0 7 17" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="_divider_x7nfb_87"><path d="M6.348 0.783999L2.54 16.464H0.986L4.794 0.783999H6.348Z" fill="currentColor"></path></svg>
                                <a href="/accounts-overview" className="text-labels-medium _link_x7nfb_12">{t('Overviewofaccounts')}</a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg width="7" height="17" viewBox="0 0 7 17" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="_divider_x7nfb_87"><path d="M6.348 0.783999L2.54 16.464H0.986L4.794 0.783999H6.348Z" fill="currentColor"></path></svg>
                                <a className="text-labels-medium _link_x7nfb_12 _activeLink_x7nfb_79">{t('AccountLiveMetriX')} {id ? id : ''}</a>
                            </div>
                        </nav> */}
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 rounded-lg border bg-white px-3.5 py-[7px] transition hover:bg-slate-50"
                                style={{ borderColor: '#d0d5dd', cursor: 'pointer' }}
                                onClick={handleAddCredentialsClick}
                            >
                                {hasContainerStatus ? (
                                    isBotRunning ? (
                                        <span className="relative h-2 w-2 flex-shrink-0">
                                            <span className="block h-2 w-2 rounded-full" style={{ background: '#00c9a7' }} />
                                            <span
                                                className="absolute inset-0 rounded-full"
                                                style={{ background: '#00c9a7', opacity: 0.6, animation: 'ping 1.4s ease-out infinite' }}
                                            />
                                        </span>
                                    ) : (
                                        <span className="block h-2 w-2 flex-shrink-0 rounded-full" style={{ background: '#d64545' }} />
                                    )
                                ) : (
                                    <span className="block h-2 w-2 flex-shrink-0 rounded-full" style={{ background: '#c0c8d4' }} />
                                )}
                                <span className="text-sm font-medium" style={{ color: '#0b1c2e' }}>
                                    {t(hasContainerStatus ? 'Bot Detail' : 'Add Credentials')}
                                </span>
                                {hasContainerStatus && (
                                    isBotRunning ? (
                                        <span className="rounded px-1.5 py-0.5 text-xs font-medium" style={{ background: '#e6faf6', color: '#00a589' }}>
                                            {t('Running')}
                                        </span>
                                    ) : (
                                        <span className="rounded px-1.5 py-0.5 text-xs font-medium" style={{ background: '#fff0f0', color: '#a32d2d' }}>
                                            {t('Stopped')}
                                        </span>
                                    )
                                )}
                                {
                                    !hasContainerStatus && (
                                        <span className="rounded px-1.5 py-0.5 text-xs font-medium" style={{ background: '#f0f0f0', color: '#606060' }}>
                                            {t('Not_Installed')}
                                        </span>
                                    )
                                }
                            </button>
                            {
                                report && !isNoData && (
                                    <Link to={`/${tradePlatform}/${id}/pdf-custom`} target="_blank" rel="noopener noreferrer" className="">
                                        {report && <Button style={{}}><Download className="w-4 h-4" />{t('ExportPDF')}</Button>}
                                    </Link>
                                )
                            }
                        </div>

                    </div>

                    {/* Header Section */}
                    <div className='pdf-section'>
                        <div className="rounded-1 bg-background-secondary border border-cts-gray-200 fix-boder-head">
                            <div className="_sectionHeaderItem_1aplf_3 _itemWithPadding_1aplf_8">
                                <div className="_mainSectionContainer_1aplf_56">
                                    <div className="_itemTitleWrapper_1aplf_32 justify-between md:flex-row flex-col w-full" >
                                        <div className="inline-flex align-items-center gap-4">
                                            <span className="_itemIcon_1aplf_24">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <g clipPath="url(#clip0_6_2798)">
                                                        <path d="M2.59566 21.4054V0H0V24H24V21.4054H2.59566Z" fill="currentColor"></path>
                                                        <path
                                                            d="M4.94773 11.8576H6.86175C7.22307 11.8576 7.56485 11.6526 7.73087 11.3305L10.4945 6.01051C10.5628 5.88361 10.7484 5.89337 10.7679 5.89337C10.7972 5.89337 10.9534 5.91289 10.9925 6.01051L14.8596 15.8988C15.1721 16.709 15.9631 17.2361 16.881 17.2556C16.9006 17.2556 16.9103 17.2556 16.9299 17.2556C17.8283 17.2556 18.6193 16.7578 18.9611 15.9769L21.2852 10.7057H23.0235C23.5606 10.7057 24 10.2665 24 9.72959C24 9.19272 23.5606 8.75345 23.0235 8.75345H20.6505C20.2599 8.75345 19.9083 8.97796 19.7521 9.33914L17.174 15.1862C17.1252 15.3033 16.9592 15.3131 16.9201 15.3131C16.8615 15.3131 16.7248 15.3033 16.676 15.196L12.8089 5.29792C12.4964 4.50725 11.7542 3.9899 10.8656 3.94109C9.96714 3.89229 9.16638 4.34131 8.766 5.1027L6.27582 9.9053H4.94773C4.41063 9.9053 3.97119 10.3446 3.97119 10.8814C3.97119 11.4183 4.41063 11.8576 4.94773 11.8576Z"
                                                            fill="currentColor"
                                                        ></path>
                                                    </g>
                                                    <defs>
                                                        <clipPath id="clip0_6_2798"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                                                    </defs>
                                                </svg>
                                            </span>
                                            <h5 className="_itemTitle_1aplf_17 text-headings-h5 justify-between w-full">
                                                <div className="inline-flex align-items-center gap-4">
                                                    <h5 className="mb-0">{t('AccMetriX')} {id}</h5>
                                                </div>

                                            </h5>
                                        </div>
                                        <div className="inline-flex items-center gap-2 w-fit float-end hide-mod">
                                            <p className="text-labels-small text-content-secondary m-0">
                                                {t('Time_filter')}:
                                            </p>
                                            <DatePicker
                                                placeholder={t('start_date')}
                                                value={dayjsFrom}
                                                allowClear={false}
                                                format="YYYY-MM-DD"
                                                onChange={
                                                    (date, dateString) => {
                                                        if (date) {
                                                            setCloseTimeFrom(date.format('YYYY-MM-DD'));
                                                        } else {
                                                            setCloseTimeFrom(''); // Set to empty string instead of '*'
                                                        }
                                                    }
                                                } />
                                            <DatePicker
                                                placeholder={t('end_date')}
                                                value={dayjsTo}
                                                allowClear={false}
                                                format="YYYY-MM-DD"
                                                onChange={
                                                    (date, dateString) => {
                                                        if (date) {
                                                            setCloseTimeTo(date.format('YYYY-MM-DD'));
                                                        } else {
                                                            setCloseTimeTo(''); // Set to empty string instead of '*'
                                                        }
                                                    }
                                                } />
                                        </div>
                                    </div>
                                    <div></div>
                                </div>
                            </div>
                            <div className="_contentBoxTransitionWrapper_1aplf_90 _close_1aplf_106">
                                <div className="p-3 md:p-6"><div className="m-0 text-content-secondary"></div></div>
                            </div>
                        </div>
                    </div>
                    <div className="border-b border-slate-200 mt-6">
                        <nav className="-mb-px flex space-x-8">
                            <a href={`/academy?tab=checklist&date=${(() => { const d = new Date(); return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`; })()}&step=0`}
                                className="tab-inactive whitespace-nowrap !no-underline !py-3 !px-1 text-sm flex items-center gap-2">
                                <svg className="w-4 h-4 !text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                                Checklist hàng ngày
                            </a>
                            <a href={`/${tradePlatform}/account-metrix/${id}`} className="tab-active whitespace-nowrap !no-underline !py-3 !px-1 text-sm flex items-center gap-2 transition-colors">
                                <svg className="w-4 h-4 !text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Bảng phân tích
                            </a>
                            <a href={`/${tradePlatform}/account-live-metrix/${id}`} className="tab-inactive whitespace-nowrap !no-underline !py-3 !px-1 text-sm flex items-center gap-2 transition-colors">
                                <svg className="w-4 h-4 !text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                Chi tiết
                            </a>
                        </nav>
                    </div>

                    {/* Row 1: Charts & Account Info */}
                    <div className="grid xl:grid-cols-3 sm:grid-cols-1 gap-6 !border-none ring-0 !shadow-none mt-6">
                        <div className="xl:col-span-2 bg-white rounded-xl shadow-premium border border-slate-100 p-6 flex flex-col relative overflow-hidden">
                            <div className="flex justify-between items-center mb-6 relative z-10">
                                <h2 className="!text-base font-bold !text-slate-900">Kết quả hiện tại</h2>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-6 relative z-10">
                                {[
                                    { label: 'Số dư tài khoản', value: sectionChartData.currentBalance ? `$${sectionChartData.currentBalance.toLocaleString()}` : '$0.00' },
                                    { label: 'Vốn chủ sở hữu', value: sectionChartData.equity ? `$${sectionChartData.equity.toLocaleString()}` : '$0.00', isEmpty: !sectionChartData.equity, color: sectionChartData.equity < 0 ? '!text-rose-600' : '!text-emerald-600' },
                                    { label: 'Lãi/lỗ chưa thực hiện', value: sectionChartData.profitLoss ? `${sectionChartData.profitLoss > 0 ? `$${sectionChartData.profitLoss.toLocaleString()}` : `-$${Math.abs(sectionChartData.profitLoss).toLocaleString()}`}` : 'Không có dữ liệu', isEmpty: !sectionChartData.profitLoss, color: sectionChartData.profitLoss < 0 ? '!text-rose-600' : '!text-emerald-600' },
                                ].map((kpi, idx) => (
                                    <div key={idx}>
                                        <div className="flex items-center gap-1 mb-2">
                                            <p className="!text-sm !text-slate-600 font-medium">{kpi.label}</p>
                                        </div>
                                        <div className="bg-slate-100/80 rounded py-2 !text-center border border-slate-200/50">
                                            {kpi.isEmpty ? (
                                                <p className="!text-xs !m-0 font-medium !text-slate-400 py-1">{kpi.value}</p>
                                            ) : (
                                                <p className={`!text-[15px] !m-0 font-bold  font-mono-num tracking-tight ${kpi.color ? kpi.color : '!text-slate-900'}`}>{kpi.value}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex-1 min-h-[300px] relative w-full mt-2">
                                <div className="absolute inset-0">
                                    <MainAccountBalanceChart
                                        data={sectionChartData.chartData.datasets}
                                        labels={sectionChartData.chartData.labels}
                                        chartPoints={sectionChartData.chartPoints}
                                        trades={report?.journal_closed_trades}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right: Account Information */}
                        <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-6 flex flex-col">
                            <h2 className="!text-base font-bold !text-slate-900 mb-6">Thông tin tài khoản</h2>
                            <div className="flex-1 flex flex-col justify-between !text-sm">
                                {[
                                    { label: 'Kết quả:', value: 'Trong quá trình', type: 'badge', color: 'bg-teal-500' },
                                    { label: 'Trạng thái:', value: report?.account_info?.account_status || 'N/A', type: 'mono' },
                                    { label: 'Số tài khoản:', value: id || 'N/A', type: 'number' },
                                    { label: 'Bắt đầu:', value: convertToLocalTime(report?.account_info?.created_at || '', 'DD/MM/YYYY') || 'N/A', type: 'text' },
                                    { label: 'Số vốn ban đầu:', value: '$' + (report?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || report?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'), type: 'number' },
                                    { label: 'Loại tài khoản:', value: report?.account_info?.broker?.toUpperCase() || 'N/A', type: 'text' },
                                    {
                                        label: 'Chỉ số căn chỉnh:',
                                        value: (
                                            <span className="flex items-center gap-1.5 cursor-pointer" >
                                                DAI <strong className="font-bold text-blue-600" title={personalNumberMessage.day}>{personalNumbers?.personalDayNumber || 'N/A'}</strong>
                                                • MRI <strong className="font-bold text-blue-600" title={personalNumberMessage.month}>{personalNumbers?.personalMonthNumber || 'N/A'}</strong>
                                                • AMI <strong className="font-bold text-blue-600" title={personalNumberMessage.year}>{personalNumbers?.personalYearNumber || 'N/A'}</strong>
                                                <EyeIcon size={16} />
                                            </span>
                                        ),
                                        type: 'info',
                                        className: 'text-xs !text-slate-400',
                                        onClick: () => setShowPersonalInfo(!showPersonalInfo)
                                    },
                                    { label: `Nền tảng ${tradePlatform?.toUpperCase() || 'N/A'}:`, value: 'Tải xuống', type: 'link' },
                                ].map((item, idx) => (
                                    <div key={idx}
                                        onClick={item.onClick}
                                        className={`flex justify-between items-center py-3 ${idx !== 7 ? 'border-b border-slate-100' : 'pt-4'}`}>
                                        <span className="!text-slate-500 font-medium">{item.label}</span>
                                        {item.type === 'badge' ? (
                                            <span className={`px-3 py-1 rounded-full !text-xs font-bold !text-white shadow-sm ${item.color}`}>{item.value}</span>
                                        ) : item.type === 'mono' ? (
                                            <span className="font-bold !text-slate-900 bg-slate-100 px-2 py-0.5 rounded !text-xs uppercase tracking-wider">{item.value}</span>
                                        ) : item.type === 'bot' ? (
                                            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded !text-xs font-bold bg-emerald-50 !text-emerald-600 border border-emerald-200 shadow-sm">
                                                <span className="relative flex h-1.5 w-1.5">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                                </span>
                                                {item.value}
                                            </span>
                                        ) : item.type === 'number' ? (
                                            <span className="font-bold !text-slate-900 font-mono-num">{item.value}</span>
                                        ) : item.type === 'link' ? (
                                            <a href="#" className="font-medium !text-brand-600 hover:!text-brand-700 hover:underline">{item.value}</a>
                                        ) : (
                                            <span className="font-medium !text-slate-800">{item.value}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Trading Statistics Section */}
                    {report && (
                        <TradingStatistics report={report} liveTrade={liveTrade} />
                    )}

                    <div className="flex flex-col gap-6 md:flex-row w-full flex-grow mt-6">
                        {/* ConsistencyScore chỉ chiếm đúng chiều rộng tự nhiên, không bị méo */}
                        <div className="w-full md:w-auto flex-shrink-0">
                            <ConsistencyScore score={report?.consistency_score || 0} />
                        </div>

                        {/* AccountGoals sử dụng flex-1 để chiếm trọn phần không gian còn dư */}
                        <div className="flex-1 w-full min-w-0">
                            <AccountGoals report={report} />
                        </div>
                    </div>

                    {/* Consistency Score */}
                    {/* <ConsistencyScore report={report} /> */}

                    {/* Behavioral Analysis Section */}
                    {report?.behavioral_analysis && (
                        <BehavioralAnalysis report={report} />
                    )}


                    {/* Intraday Strategy Performance Chart */}
                    {/* {report?.intradays_strategy_performance && (
                        <div className="bg-background-secondary rounded-1 mt-6 p-6 border border-cts-gray-200 w-full pdf-section no-boder">
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-headings-h6 text-content-primary"><Tooltip title="Performance of your strategies within the trading day, by intraday intervals."><span>{t('IntradayStrategyPerformance')}</span></Tooltip></h3>
                            </div>
                            <PerformanceChart data={report?.intradays_strategy_performance} />
                        </div>
                    )} */}

                    {/* Time Analysis Charts */}
                    {report?.time_analysis && (
                        <div className="bg-background-secondary rounded-1 mt-6 p-6 border border-cts-gray-200 w-full pdf-section no-boder">
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-headings-h6 text-content-primary"><Tooltip title={t('tooltip_time_analysis')}><span>{t('TimebasedAnalysis')}</span></Tooltip></h3>
                            </div>
                            <DashboardCharts
                                monthlyAnalysis={report?.time_analysis.monthly_analysis}
                            />
                        </div>
                    )}
                    {/* Day in Week Analysis Chart */}
                    {report?.time_analysis && (
                        <div className="bg-background-secondary rounded-1 mt-6 p-6 border border-cts-gray-200 pdf-section no-boder">
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-headings-h6 text-content-primary"><Tooltip title={t('tooltip_time_analysis')}><span>{t('TimebasedDayInWeekAnalysis')}</span></Tooltip></h3>
                            </div>
                            <DashboardCharts2
                                dailyAnalysis={report?.time_analysis.daily_analysis}
                            />
                        </div>
                    )}
                    {report?.time_analysis && (
                        <div className="bg-background-secondary rounded-1 mt-6 p-6 border border-cts-gray-200 pdf-section no-boder">
                            <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-headings-h6 text-content-primary"><Tooltip title={t('tooltip_time_analysis')}><span>{t('TimebasedHourAnalysis')}</span></Tooltip></h3>
                            </div>
                            <DashboardCharts3
                                hourlyAnalysis={report?.time_analysis.hourly_analysis}
                            />
                        </div>
                    )}

                    {/* Equity Simulation Section */}
                    {/* <EquitySimulation report={report} liveTrade={liveTrade} /> */}

                    {/* Daily Summary Section */}
                    <DailySummary report={report} />

                    {/* Trade Summary Section */}
                    <TradeHistoryList report={report} />

                    {/* Footer Section */}
                    <Footer />
                    <Modal
                        open={showPersonalInfo}
                        title={t('BehaviourSection1Title')}
                        onCancel={() => setShowPersonalInfo(false)}
                        footer={null}
                    >
                        <div className="space-y-4 overflow-y-auto max-h-[70vh]" style={{ scrollbarWidth: "none" }}>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-semibold">DAI</h3>
                                    <span className="text-2xl font-bold text-blue-600">
                                        {personalNumbers.personalDayNumber ?? "-"}
                                    </span>
                                </div>
                                <p className="text-gray-600">{personalNumberMessage.day}</p>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-semibold">MRI</h3>
                                    <span className="text-2xl font-bold text-blue-600">
                                        {personalNumbers.personalMonthNumber ?? "-"}
                                    </span>
                                </div>
                                <p className="text-gray-600">{personalNumberMessage.month}</p>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-semibold">AMI</h3>
                                    <span className="text-2xl font-bold text-blue-600">
                                        {personalNumbers.personalYearNumber ?? "-"}
                                    </span>
                                </div>
                                <p className="text-gray-600">{personalNumberMessage.year}</p>
                            </div>
                        </div>
                    </Modal>
                </div >
                <ContainerStatusModal
                    isOpen={isContainerStatusModalOpen}
                    statusData={containerStatus}
                    logsData={containerLogs}
                    isLoading={isContainerStatusLoading}
                    errorMessage={containerStatusError}
                    onClose={() => setIsContainerStatusModalOpen(false)}
                    onRestart={handleRestartCredentialsFlow}
                />
                <CredentialsModal
                    isOpen={isCredentialsModalOpen}
                    values={accountCredentials}
                    tradePlatform={tradePlatform || ''}
                    isSaving={isCredentialsSaving}
                    errorMessage={credentialsError}
                    onClose={() => setIsCredentialsModalOpen(false)}
                    onChange={handleCredentialFieldChange}
                    onSave={handleCredentialsSave}
                />
                <CredentialsConfirmationModal
                    isOpen={isCredentialsConfirmationOpen}
                    title={t('Lumir_bot.Title.add_success')}
                    description={t('Lumir_bot.message.success')}
                    actionLabel={t('Lumir_bot.done')}
                    onClose={() => setIsCredentialsConfirmationOpen(false)}
                />
            </Layout >
        </>
    );
}


export default DashBoardMetrix;